import { useMemo } from 'react';

import path from 'path';

import { Stack, Typography } from '@mui/material';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair } from 'react-router-dom';

import { Card, DataGrid } from '~components';
import useColDefs from '~components/dataGrids/useColDefs';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import Device from '~interfaces/device';
import { Dimens } from '~themes';

const persistenceKey = 'devices';

interface Props {
	overwrittenClientId?: string;
	isLoading: boolean;
	showTitle: boolean;
	includeOrganisation?: boolean;
	devices: Device[];
}

const DeviceGroupDevicesDataGrid = ({
	overwrittenClientId,
	isLoading = false,
	showTitle = false,
	includeOrganisation = false,
	devices = [],
}: Props) => {
	const { t } = useTranslation();
	const apiRef = useGridApiRef();

	const colDefs = useColDefs<Device>();
	const columns = useMemo((): GridColDef<Device>[] => {
		return [
			colDefs.id(
				path.join(pagePathSegments.Devices, ':id', pagePathSegments.General),
				(value: Device) => {
					return overwrittenClientId ?
							[
								[
									queryParameters.OverwrittenClientId,
									overwrittenClientId ??
										value.organisationId ??
										generalInformation.manufacturingOrganisationId,
								],
							]
						:	([] as ParamKeyValuePair[]);
				}
			),
			colDefs.hardwareId((value: Device) => {
				return overwrittenClientId ?
						[
							[
								queryParameters.OverwrittenClientId,
								overwrittenClientId ??
									value.organisationId ??
									generalInformation.manufacturingOrganisationId,
							],
						]
					:	([] as ParamKeyValuePair[]);
			}),
			colDefs.name(
				true,
				path.join(pagePathSegments.Devices, ':id', pagePathSegments.General),
				(value: Device) => {
					return overwrittenClientId ?
							[
								[
									queryParameters.OverwrittenClientId,
									overwrittenClientId ??
										(value as Device).organisationId ??
										generalInformation.manufacturingOrganisationId,
								],
							]
						:	([] as ParamKeyValuePair[]);
				}
			),
			colDefs.deviceModel(),
			...(includeOrganisation ?
				[colDefs.organisationName(true, (value: Device) => value.organisationId ?? '')]
			:	[]),
			colDefs.resourceGroup(),
		];
	}, [colDefs, includeOrganisation, overwrittenClientId, t]);

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				{showTitle && (
					<Typography variant='labelLg'>{t('device.labels.plural', { ns: 'entities' })}</Typography>
				)}
				<DataGrid
					apiRef={apiRef}
					getRowId={(row) => row.id}
					persistenceKey={persistenceKey}
					editMode='row'
					autoHeight
					disableRowSelectionOnClick
					loading={isLoading}
					columns={columns}
					rows={devices}
					disableRowSelectionExcludeModel
					keepNonExistentRowsSelected
					paginationMode='client'
				/>
			</Stack>
		</Card>
	);
};
export default DeviceGroupDevicesDataGrid;
